import React, { useState, useEffect } from 'react';
import './AdminPanel.css';
import FileManager from './FileManager';

const AdminPanel = ({ username, onClose, apiUrl }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [username]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${apiUrl}/api/admin/analytics?username=${username}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al obtener datos');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('[AdminPanel] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays} dias`;

    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getRoleBadgeClass = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin': return 'role-admin';
      case 'editor': return 'role-editor';
      case 'viewer': return 'role-viewer';
      default: return 'role-default';
    }
  };

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="admin-header">
          <h2>Panel de Administracion</h2>
          <button className="admin-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Cargando datos de actividad...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-panel">
        <div className="admin-header">
          <h2>Panel de Administracion</h2>
          <button className="admin-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="admin-error">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button className="retry-btn" onClick={fetchAnalytics}>Reintentar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div className="admin-header-left">
          <h2>Panel de Administracion</h2>
          <span className="org-badge">{data?.organization?.displayName}</span>
        </div>
        <button className="admin-close-btn" onClick={onClose}>✕</button>
      </div>

      {/* Resumen General */}
      <div className="admin-section">
        <h3 className="section-title">Resumen General</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-icon">👥</span>
            <div className="stat-info">
              <span className="stat-value">{data?.summary?.total_users || 0}</span>
              <span className="stat-label">Usuarios Totales</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">✅</span>
            <div className="stat-info">
              <span className="stat-value">{data?.summary?.active_last_7_days || 0}</span>
              <span className="stat-label">Activos (7 dias)</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">📊</span>
            <div className="stat-info">
              <span className="stat-value">{data?.summary?.active_last_30_days || 0}</span>
              <span className="stat-label">Activos (30 dias)</span>
            </div>
          </div>
        </div>

        <div className="roles-summary">
          <div className="role-item">
            <span className="role-badge role-admin">Admin</span>
            <span className="role-count">{data?.summary?.admin_count || 0}</span>
          </div>
          <div className="role-item">
            <span className="role-badge role-editor">Editor</span>
            <span className="role-count">{data?.summary?.editor_count || 0}</span>
          </div>
          <div className="role-item">
            <span className="role-badge role-viewer">Viewer</span>
            <span className="role-count">{data?.summary?.viewer_count || 0}</span>
          </div>
        </div>
      </div>

      {/* Tabla de Usuarios */}
      <div className="admin-section">
        <h3 className="section-title">Actividad de Usuarios</h3>
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Rol</th>
                <th>Ultimo Acceso</th>
                <th>Accesos PBI</th>
                <th>Mensajes Chat</th>
              </tr>
            </thead>
            <tbody>
              {data?.users?.map((user, index) => (
                <tr key={index}>
                  <td className="user-cell">
                    <span className="user-avatar">
                      {user.username?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                    <span className="user-name-cell">{user.username}</span>
                  </td>
                  <td>
                    <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="date-cell">
                    {formatDate(user.last_login_at)}
                  </td>
                  <td className="number-cell">
                    {user.total_powerbi_accesses || 0}
                  </td>
                  <td className="number-cell">
                    {user.total_chatbot_messages || 0}
                  </td>
                </tr>
              ))}
              {(!data?.users || data.users.length === 0) && (
                <tr>
                  <td colSpan="5" className="empty-row">
                    No hay usuarios registrados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reportes Mas Vistos */}
      <div className="admin-section">
        <h3 className="section-title">Reportes Mas Vistos (30 dias)</h3>
        <div className="reports-list">
          {data?.topReports?.map((report, index) => (
            <div key={index} className="report-item">
              <div className="report-rank">#{index + 1}</div>
              <div className="report-info">
                <span className="report-name">{report.report_name}</span>
                <span className="report-meta">
                  {report.unique_users} usuarios • Ultimo: {formatDate(report.last_access)}
                </span>
              </div>
              <div className="report-views">
                <span className="views-count">{report.total_views}</span>
                <span className="views-label">vistas</span>
              </div>
            </div>
          ))}
          {(!data?.topReports || data.topReports.length === 0) && (
            <div className="empty-reports">
              No hay datos de reportes en los ultimos 30 dias
            </div>
          )}
        </div>
      </div>

      {/* Gestor de Archivos - Solo si tiene acceso configurado */}
      {data?.hasFileAccess && (
        <div className="admin-section">
          <FileManager username={username} apiUrl={apiUrl} />
        </div>
      )}

    </div>
  );
};

export default AdminPanel;
